/**
 * Neo 内部的自定义埋点模块
 * @description 内部有2个指标： 页面加载时间(TTI)、桥调用时长
 * 页面订阅了 DOMContentLoaded 事件
 */

import { getProp } from '@mx/util/get-prop';
import { hasSessionStorage, setSessionStorage } from '../util/storage';
import { MetricChannel } from './channel/metric-channel';
import { MetricSendParams } from './constants';

/**
 * TTI 加载的每个阶段
 */
export enum TTIStage {
  // Activity 加载耗时
  LoadActivity = 'loadActivity',
  // Webview 加载耗时
  LoadWebview = 'loadWebview',
  // H5 页面 DOM 加载耗时
  DOMContentLoaded = 'DOMContentLoaded',
  // H5 onLoad 事件耗时
  PageLoad = 'pageLoad',
  // Native 整体耗时
  TotalNative = 'totalNative',
  // 页面渲染完成耗时
  TotalTTI = 'renderFinished',
}

/**
 * 纬度参数
 */
/* eslint-disable camelcase */
export interface NeoMetricInitParams {
  nb_app: string;
  stat_time: string;
  nb_platform: string;
  neo_sdk_version: string;
  sdk_version: string;
  icashier_version: string;
  neo_shark: string;
  nsr: string;
  app_offline: string;
}
/* eslint-enable camelcase */

/**
 * Neo 专用自定义上报
 */
export class NeoMetric {
  // 标示参数是否被初始化
  isParamsReady = false;
  // 从参数隧道读取的起点时间
  statTime: string[] = [];

  private commonMetric = new MetricChannel();
  // 延迟上报埋点数组，等待全量参数获取后上报
  private delayReportList = [] as MetricSendParams[];

  constructor() {
    // TODO 使用 Performance API 统计耗时
    // 上报 DOMContentLoaded 耗时
    window.addEventListener(
      'DOMContentLoaded',
      () => {
        this.sendTTI(TTIStage.DOMContentLoaded, this.getTimeDiff(0));
      },
      { once: true }
    );
    window.addEventListener(
      'load',
      () => {
        this.sendTTI(TTIStage.PageLoad, this.getTimeDiff(0));
      },
      { once: true }
    );
  }

  setTags(tags: PureObject = {}): void {
    this.commonMetric.setMetricTags({ ...tags });
    this.isParamsReady = true;
  }

  setStatTimeAndReport(time: string = '') {
    if (time) {
      this.statTime = time.split(',') || [];
      this.sendNativeDuration();
    }
  }

  /**
   * 通用上报对象
   * @param data 数据
   */
  report(data: MetricSendParams): void {
    if (!this.isParamsReady) {
      this.delayReportList.push(data);
      return;
    }
    this.commonMetric.send(data);
  }

  /**
   * 发送所有延迟上报埋点
   */
  sendDelayReportList(): void {
    if (this.isParamsReady && this.delayReportList.length) {
      this.delayReportList.map((item) => this.commonMetric.send(item));
      this.delayReportList.length = 0;
    }
  }

  /**
   * 上报桥耗时
   * @param bridge
   * @param time
   */
  sendBridgeDuration(tags: PureObject, time: number): void {
    const info = {
      key: 'bridgeDuration',
      value: time,
      tags,
    };
    this.report(info);
  }

  /**
   * 上报Neo SDK阶段的耗时数据
   */
  sendNativeDuration(): void {
    if (!this.statTime || this.statTime.length <= 0) {
      return;
    }
    // loadActivity - nativeInit
    if (Number(this.statTime[1])) {
      this.sendTTI(
        TTIStage.LoadActivity,
        Number(this.statTime[1]) - Number(this.statTime[0])
      );
    }
    // loadWebview - loadActivity
    if (Number(this.statTime[2])) {
      this.sendTTI(
        TTIStage.LoadWebview,
        Number(this.statTime[2]) - Number(this.statTime[1])
      );
    }
    // loadWebview - nativeInit
    if (Number(this.statTime[3])) {
      this.sendTTI(
        TTIStage.LoadWebview,
        Number(this.statTime[3]) - Number(this.statTime[0])
      );
    }
  }

  /**
   * 对外公布一个页面渲染完成时上报TTI的方法
   */
  sendPageRenderFinished() {
    // monitor.timeMark('ttiEnd');
    this.sendTTI(TTIStage.TotalTTI, this.getTimeDiff(0));
  }

  /**
   * 上报 TTI 数据
   * @param stage 阶段
   * @param time  时间
   */
  private sendTTI(stage: TTIStage, time: number): void {
    if (time < 0 || time > 200000 || hasSessionStorage(stage)) {
      return;
    }
    setSessionStorage(stage);
    this.report({
      key: 'totalTTI',
      value: time,
      tags: {
        stage,
      },
    });
  }

  /**
   * 基于 stat_time 计算时间差
   * @param index 索引：0 ~ 2
   */
  private getTimeDiff(index: number): number {
    const startTime = getProp(this.statTime, [index], 0) * 1;
    return Date.now() - startTime;
  }
}

const neoMetric = new NeoMetric();
export default neoMetric;
